import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi, ProductOption } from '@/api/products';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Settings,
} from 'lucide-react';

export function OptionFormPage() {
  const { productCode, optionId } = useParams<{ productCode: string; optionId: string }>();
  const navigate = useNavigate();
  const isEdit = !!optionId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productName, setProductName] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'recurring' | 'one_shot'>('recurring');
  const [price, setPrice] = useState<string>('');
  const [priceT2, setPriceT2] = useState<string>('');
  const [priceT3, setPriceT3] = useState<string>('');
  const [priceT4, setPriceT4] = useState<string>('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    loadData();
  }, [productCode, optionId]);

  const loadData = async () => {
    if (!productCode) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch product to get name
      const product = await productsApi.getByCode(productCode);
      setProductName(product.name);

      // If editing, find the option
      if (isEdit && optionId) {
        const option = product.options.find(o => o.id === parseInt(optionId));
        if (option) {
          setName(option.name);
          setCode(option.code);
          setDescription(option.description || '');
          setType(option.type);
          setPrice(option.price?.toString() || '');
          setPriceT2(option.priceT2?.toString() || '');
          setPriceT3(option.priceT3?.toString() || '');
          setPriceT4(option.priceT4?.toString() || '');
          setDisplayOrder(option.displayOrder);
          setIsActive(option.isActive);
        } else {
          setError('Option non trouvee');
        }
      }
    } catch (err) {
      setError('Erreur lors du chargement des donnees');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productCode) return;
    if (!name.trim() || !code.trim()) {
      setError('Le nom et le code sont requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const data = {
        name,
        code,
        description: description || undefined,
        type,
        price: price ? parseFloat(price) : null,
        priceT2: priceT2 ? parseFloat(priceT2) : null,
        priceT3: priceT3 ? parseFloat(priceT3) : null,
        priceT4: priceT4 ? parseFloat(priceT4) : null,
        displayOrder,
        isActive,
      };

      if (isEdit && optionId) {
        await productsApi.updateOption(productCode, parseInt(optionId), data);
      } else {
        await productsApi.createOption(productCode, data);
      }

      navigate(`/products/${productCode}`);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/products" className="hover:text-foreground">Produits</Link>
        <span>/</span>
        <Link to={`/products/${productCode}`} className="hover:text-foreground">{productName}</Link>
        <span>/</span>
        <span className="text-foreground">{isEdit ? `Modifier ${name}` : 'Nouvelle option'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" type="button" asChild>
              <Link to={`/products/${productCode}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Settings className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isEdit ? `Modifier l'option ${name}` : 'Nouvelle option'}
            </h1>
          </div>
        </div>

        <Button type="submit" disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* General Info */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-base">Informations generales</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="SUPPORT"
                disabled={isEdit}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Support technique"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description de l'option..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as 'recurring' | 'one_shot')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurring">Recurrent (annuel)</SelectItem>
                  <SelectItem value="one_shot">One-shot (unique)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayOrder">Ordre d'affichage</Label>
              <Input
                id="displayOrder"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                min={0}
              />
            </div>
            <div className="flex items-center justify-between space-x-2 pt-6">
              <Label htmlFor="isActive">Option active</Label>
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-base">Tarification par tranche</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">T1 (0-100 élèves)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT2">T2 (101-250 élèves)</Label>
              <Input
                id="priceT2"
                type="number"
                step="0.01"
                min="0"
                value={priceT2}
                onChange={(e) => setPriceT2(e.target.value)}
                placeholder="0.00"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT3">T3 (251-500 élèves)</Label>
              <Input
                id="priceT3"
                type="number"
                step="0.01"
                min="0"
                value={priceT3}
                onChange={(e) => setPriceT3(e.target.value)}
                placeholder="0.00"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priceT4">T4 (501+ élèves)</Label>
              <Input
                id="priceT4"
                type="number"
                step="0.01"
                min="0"
                value={priceT4}
                onChange={(e) => setPriceT4(e.target.value)}
                placeholder="0.00"
                className="font-mono"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
